import { useState, useCallback, useEffect } from 'react';
import { Conversation } from '../../../types/chat';
import { storage } from '../../../lib/storage';
import { notify } from '../../../utils/toast';
import { getApiUrl } from '../../../config';
import { useMessages } from './useMessages';
import { fetchConversations } from '../../../services/api/conversationApi';
import { 
  fetchSharedToConversations, 
  fetchSharedByConversations,
  updateSharedConversations 
} from '../../../services/api/sharedConversationsApi';
import { getAuthData } from "../../../contexts/AuthContext";

export const useConversations = (onSelectConversationRef?: React.MutableRefObject<((conversation: Conversation) => Promise<void>) | null>) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [hasNewChat, setHasNewChat] = useState(false);
  const [loading, setLoading] = useState(false);


  // Load conversations on mount
  useEffect(() => {
    const loadConversations = async () => {
      const walletAddress = localStorage.getItem('walletAddress');
      if (!walletAddress) return;

      try {
        setLoading(true);
        const storedConversations = await storage.getConversations(walletAddress);
        // Ensure isPinned is properly initialized for all conversations
        const initializedConversations = storedConversations.map(conv => ({
          ...conv,
          isPinned: conv.isPinned || false
        }));
        setConversations(initializedConversations);
      } catch (error) {
        console.error('Error loading conversations:', error);
        notify.error('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, []);

  /**
   * Synchronize conversations with the backend
   * This will fetch conversations from the API and update local storage
   */
  const syncConversations = useCallback(async () => {
    try {
      const auth = await getAuthData();
      
      // Fetch regular conversations
      const apiConversations = await fetchConversations(auth);
      
      // Fetch shared conversations
      const sharedToIdsObjects = await fetchSharedToConversations(auth);
      const sharedToIds = sharedToIdsObjects.map(obj=> {return {id: obj?.name.split('_')[1], name: obj?.name.split('_')[2]}}); // Extract conversation IDs from keys
      const sharedByIdsObjects = await fetchSharedByConversations(auth);
      const sharedByIds = sharedByIdsObjects.map(obj=> obj?.split('_')[1]); // Extract conversation IDs from keys
      
      console.log('Shared to conversations:', sharedToIds);
      console.log('Shared by conversations:', sharedByIds);
      
      // Update conversations with shared status
      if (apiConversations) {
        const updatedConversations = await updateSharedConversations(
          auth.walletAddress,
          sharedToIds,
          sharedByIds,
          apiConversations
        );
        
      // Update state with fetched conversations
      setConversations(updatedConversations);
      
      // Update current conversation if it exists in the updated conversations
      if (currentConversation) {
        const updatedCurrentConversation = updatedConversations.find(
          conv => conv.id === currentConversation.id
        );
        if (updatedCurrentConversation) {
          setCurrentConversation(updatedCurrentConversation);
        }
      }
      
      return updatedConversations;
      }
      
      return apiConversations;
    } catch (error) {
      console.error('Error syncing conversations:', error);
      notify.error('Failed to sync conversations with server');
      return null;
    }
  }, [getAuthData]);

  const createConversation = useCallback(async () => {
    if (hasNewChat) return currentConversation;

    try {
      const now = new Date().toISOString();
      const newConversation: Conversation = {
        id: crypto.randomUUID(),
        title: 'New Conversation',
        created_at: now,
        updated_at: now,
        status: 'active',
        isNew: true,
        isPinned: false
      };

      setConversations(prev => [newConversation, ...prev.filter(c => !c.isNew)]);
      setCurrentConversation(newConversation);
      setHasNewChat(true);

      return newConversation;
    } catch (error) {
      console.error('Error creating conversation:', error);
      notify.error('Failed to create new conversation');
      return null;
    }
  }, [hasNewChat, currentConversation]);

  const deleteConversation = useCallback(async (conversationId: string) => {
    const walletAddress = localStorage.getItem('walletAddress');
    const profile = await storage.getProfile(walletAddress!);
    
    if (!profile?.jwt || !profile?.nonce) {
      notify.error('Profile not found');
      return;
    }

    try {
      setLoading(true);
      
      try {
        // Try to call API endpoint
        const response = await fetch(`https://ai.goplausible.xyz/agent/conversations/${conversationId}/delete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${profile.jwt}`
          },
          body: JSON.stringify({
            walletAddress,
            nonce: profile.nonce,
            user: profile.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn')
          })
        });

        if (!response.ok) {
          throw new Error('Failed to delete conversation on backend!');
        }else{
          notify.success('Conversation deleted on backend');
        }
        
      } catch (error) {
        console.error('Error deleting conversation on backend:', error);
        //notify.error('Failed to delete conversation on backend!');
      }

      // Find next most recent conversation before deletion
      let nextConversation = null;
      if (currentConversation?.id === conversationId) {
        const sortedConversations = [...conversations].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        nextConversation = sortedConversations.find(c => c.id !== conversationId) || null;
      }

      // Always remove from local storage and update state regardless of API result
      await storage.removeConversation(walletAddress!, conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      // Set next conversation as current if current was deleted
      if (currentConversation?.id === conversationId) {
        if (nextConversation) {
          setCurrentConversation(nextConversation);
          // Notify parent component to load messages
          onSelectConversationRef?.current?.(nextConversation);
        } else {
          setCurrentConversation(null);
        }
      }

      notify.success('Conversation deleted locally');
    } catch (error) {
      console.error('Error deleting conversation from local storage:', error);
      notify.error('Failed to delete conversation from local storage');
    } finally {
      setLoading(false);
    }
  }, [currentConversation]);

  return {
    conversations,
    setConversations,
    currentConversation,
    setCurrentConversation,
    hasNewChat,
    setHasNewChat,
    loading,
    setLoading,
    createConversation,
    deleteConversation,
    syncConversations
  };
};
